"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.addGuestPanelistSchema = exports.PANEL_NOT_ALLOWED_MESSAGE = exports.PANEL_ROLE_KEYS = exports.applyInterviewPlanSchema = exports.updatePlanTemplateSchema = exports.planTemplateSchema = exports.updateJobInterviewPlanSchema = exports.DEFAULT_INTERVIEW_MINUTES = exports.templateRoundSchema = exports.interviewRoundSchema = exports.panelMemberSchema = exports.PANEL_WEIGHT_TOTAL = exports.PANEL_WEIGHT_MAX = exports.PANEL_WEIGHT_MIN = exports.MAX_PANEL = exports.PANEL_ROLE_ORDER = exports.PANELIST_ROLE_LABELS = exports.DECISION_RULE_DESCRIPTIONS = exports.DECISION_RULE_LABELS = exports.INTERVIEW_MODE_LABELS = exports.MAX_INTERVIEW_ROUNDS = exports.INTERVIEW_STAGES = exports.updateInterviewKitSchema = exports.interviewKitSchema = exports.kitQuestionSchema = exports.updateRubricSchema = exports.rubricSchema = exports.rubricCriterionSchema = exports.DEFAULT_RUBRIC_SCALE_MAX = exports.RUBRIC_SCALE_MAXES = exports.RUBRIC_SCALE_MIN = void 0;
exports.criticalFloorFor = criticalFloorFor;
exports.panelWeightProblems = panelWeightProblems;
exports.equalShares = equalShares;
exports.roundNameAt = roundNameAt;
exports.defaultRound = defaultRound;
exports.roundProblems = roundProblems;
exports.roundPanelProblems = roundPanelProblems;
exports.roundSummary = roundSummary;
exports.canSitOnPanel = canSitOnPanel;
exports.panelProblems = panelProblems;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const schemas_1 = require("./schemas");
// S12 — the evaluation machinery: scorecard rubrics (ADMIN-02, F19.02), interview kits
// (ADMIN-04, F19.04), interview plan templates (ADMIN-03, F19.03) and the interview
// plan itself (JOB-07, F5.09, F5.10).
//
// Sources: master plan S12; E3 step 6; §L3 (round score, critical criteria), §L4
// (decision rules), §L5 (scheduler defaults), §L25 (config lock). Product-owner
// decisions of 2026-09-29 (Docs/DECISIONS-LOG.md S12 Q7, Q8): a template copies whole
// rounds, panel included; one round per interview stage, at most three.
// --- Rubrics (ADMIN-02) -------------------------------------------------------------
exports.RUBRIC_SCALE_MIN = 1;
/** The scales a rubric may use. Default 1–5 (master plan S12). */
exports.RUBRIC_SCALE_MAXES = [3, 4, 5, 10];
exports.DEFAULT_RUBRIC_SCALE_MAX = 5;
const MAX_RUBRIC_CRITERIA = 15;
const MAX_CRITERION_WEIGHT = 100;
/**
 * §L3: a critical criterion blocks advance when its mean is below 2.0 on a 5-point
 * scale — a quarter of the way up. The same fraction holds on any other scale.
 */
function criticalFloorFor(scaleMax) {
    return Math.round((exports.RUBRIC_SCALE_MIN + (scaleMax - exports.RUBRIC_SCALE_MIN) * 0.25) * 100) / 100;
}
exports.rubricCriterionSchema = zod_1.z.object({
    /**
     * Stays the same across rubric versions, so an interview kit question mapped to
     * "Communication" still finds it after the rubric is edited. Absent on a new line.
     */
    key: schemas_1.uuidSchema.optional(),
    label: zod_1.z.string().trim().min(1, 'Name what is being judged.').max(120),
    description: zod_1.z.string().trim().max(500).nullable().default(null),
    /** §L3: the panelist score is a weighted mean, so weights need not total anything. */
    weight: zod_1.z.number().int('Whole numbers only.').min(1, 'A weight is at least 1.').max(MAX_CRITERION_WEIGHT),
    isCritical: zod_1.z.boolean().default(false),
});
exports.rubricSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(2, 'Name the rubric.').max(120),
    description: zod_1.z.string().trim().max(1000).nullable().default(null),
    scaleMax: zod_1.z.union(exports.RUBRIC_SCALE_MAXES.map((value) => zod_1.z.literal(value))),
    criteria: zod_1.z
        .array(exports.rubricCriterionSchema)
        .min(1, 'Add at least one criterion.')
        .max(MAX_RUBRIC_CRITERIA, `At most ${MAX_RUBRIC_CRITERIA} criteria.`),
});
/** An edit saves as the next version; the one it replaces stays for everything using it. */
exports.updateRubricSchema = exports.rubricSchema.extend(schemas_1.versionedSchema.shape);
// --- Interview kits (ADMIN-04) --------------------------------------------------------
const MAX_KIT_QUESTIONS = 40;
exports.kitQuestionSchema = zod_1.z.object({
    key: schemas_1.uuidSchema.optional(),
    questionText: zod_1.z.string().trim().min(1, 'Write the question.').max(500),
    guidance: zod_1.z.string().trim().max(1000).nullable().default(null),
    /** The rubric line this question gathers evidence for (master plan S12). */
    criterionKey: schemas_1.uuidSchema,
});
exports.interviewKitSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(2, 'Name the interview kit.').max(120),
    description: zod_1.z.string().trim().max(1000).nullable().default(null),
    /** Which rubric the questions map to — any of its versions, matched by criterion key. */
    rubricFamilyId: schemas_1.uuidSchema,
    questions: zod_1.z
        .array(exports.kitQuestionSchema)
        .min(1, 'Add at least one question.')
        .max(MAX_KIT_QUESTIONS, `At most ${MAX_KIT_QUESTIONS} questions.`),
});
exports.updateInterviewKitSchema = exports.interviewKitSchema.extend(schemas_1.versionedSchema.shape);
// --- Interview rounds (JOB-07) --------------------------------------------------------
/**
 * One round per interview stage, in the pipeline's fixed order (S12 Q8). Round 1 is
 * `interview_1`; adding round 3 switches `interview_3` on.
 */
exports.INTERVIEW_STAGES = [
    'interview_1',
    'interview_2',
    'interview_3',
];
exports.MAX_INTERVIEW_ROUNDS = exports.INTERVIEW_STAGES.length;
exports.INTERVIEW_MODE_LABELS = {
    online: 'Online',
    in_person: 'In person',
    phone: 'Phone',
};
exports.DECISION_RULE_LABELS = {
    unanimous: 'Unanimous',
    majority: 'Majority',
    lead_decides: 'Lead decides',
    any_veto: 'Any veto',
};
/** §L4 in one line each — shown under the choice so nobody picks blind. */
exports.DECISION_RULE_DESCRIPTIONS = {
    unanimous: 'Advance only if every panelist passes the candidate.',
    majority: 'Advance if most panelists pass. An even split goes to the lead, then the hiring manager.',
    lead_decides: "The round lead's call, whatever the others say. Needs a lead on the panel.",
    any_veto: 'One fail from any panelist stops the candidate.',
};
/** What the recruiter sees. `counting` is the schema's word for a panelist who counts (§L3). */
exports.PANELIST_ROLE_LABELS = {
    lead: 'Lead',
    counting: 'Panelist',
    observer: 'Observer',
};
/** The order the panel role choice shows them in. */
exports.PANEL_ROLE_ORDER = ['lead', 'counting', 'observer'];
/** Workflow review C5 (DECISIONS-LOG 2026-11-01 Q4): a panel of up to five people. */
exports.MAX_PANEL = 5;
/**
 * C5 / §L3 "Weighted marks": each counting panelist's share of the round result, in whole
 * percent. Null on every member means equal shares; HR may set them, each at least 10% and at
 * most 80%, totalling 100%. A panel of one carries 100% (DECISIONS-LOG 2026-11-01 Q4).
 */
exports.PANEL_WEIGHT_MIN = 10;
exports.PANEL_WEIGHT_MAX = 80;
exports.PANEL_WEIGHT_TOTAL = 100;
exports.panelMemberSchema = zod_1.z.object({
    userId: schemas_1.uuidSchema,
    role: zod_1.z.enum(enums_1.PANELIST_ROLES),
    /** Whole percent; null = an equal share. A panel saved before C5 has none, which reads as null. */
    weightPercent: zod_1.z.number().int('Whole percent only.').min(1).max(exports.PANEL_WEIGHT_TOTAL).nullable().default(null),
});
/**
 * §L3 (C5): what is wrong with a panel's shares, in plain words; empty when they stand.
 * Observers (kept from before C5) carry no share. Equal shares (every weight null) always stand.
 */
function panelWeightProblems(panel) {
    const counting = panel.filter((member) => member.role !== 'observer');
    if (counting.every((member) => member.weightPercent === null))
        return [];
    if (counting.some((member) => member.weightPercent === null))
        return ['Give everyone a share, or set the shares back to equal.'];
    const weights = counting.map((member) => member.weightPercent ?? 0);
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    if (counting.length === 1)
        return weights[0] === exports.PANEL_WEIGHT_TOTAL ? [] : ['A panel of one carries 100%.'];
    const problems = [];
    if (weights.some((weight) => weight < exports.PANEL_WEIGHT_MIN))
        problems.push(`Each person carries at least ${exports.PANEL_WEIGHT_MIN}%.`);
    if (weights.some((weight) => weight > exports.PANEL_WEIGHT_MAX))
        problems.push(`Nobody carries more than ${exports.PANEL_WEIGHT_MAX}%.`);
    if (total !== exports.PANEL_WEIGHT_TOTAL)
        problems.push(`The shares add up to ${total}%. They must make ${exports.PANEL_WEIGHT_TOTAL}%.`);
    return problems;
}
/**
 * Equal shares as whole percent, for showing and as the starting point when HR edits them: the
 * largest-remainder split, so 3 people read 34 / 33 / 33 and always total 100. The round result
 * itself uses exact equal shares (§L3), never these rounded ones.
 */
function equalShares(count) {
    if (count <= 0)
        return [];
    const base = Math.floor(exports.PANEL_WEIGHT_TOTAL / count);
    const extra = exports.PANEL_WEIGHT_TOTAL - base * count;
    return Array.from({ length: count }, (_, index) => base + (index < extra ? 1 : 0));
}
const roundShape = {
    name: zod_1.z.string().trim().min(1, 'Name the round.').max(80),
    mode: zod_1.z.enum(enums_1.INTERVIEW_MODES),
    durationMinutes: zod_1.z.number().int('Whole minutes only.').min(15, 'At least 15 minutes.').max(480),
    /** §L5: the gap the scheduler leaves after each slot. Slot length = duration + buffer. */
    bufferMinutes: zod_1.z.number().int('Whole minutes only.').min(0).max(120),
    /** §L5: the earliest a slot may start, counted from now. */
    minimumNoticeHours: zod_1.z.number().int('Whole hours only.').min(0).max(336),
    /** One exact rubric version — a later edit makes a new version and leaves this alone. */
    rubricId: schemas_1.uuidSchema.nullable(),
    interviewKitId: schemas_1.uuidSchema.nullable(),
    decisionRule: zod_1.z.enum(enums_1.DECISION_RULES),
    /** Master plan S12: across rounds, feedback is open by default. */
    isPriorFeedbackVisible: zod_1.z.boolean(),
    /** Master plan S12: within a round, blind by default. Enforced server-side (#9). */
    isBlindScoring: zod_1.z.boolean(),
    /** §L3: default 3.0 on a 5-point scale. Checked against the rubric's scale. */
    advanceThreshold: zod_1.z
        .number()
        .min(0)
        .max(10)
        .refine((value) => Math.abs(value * 10 - Math.round(value * 10)) < 1e-9, {
        message: 'Use one decimal place at most, for example 3.5.',
    }),
    /** §L4: hours after the interview ends before the escalation chain starts. */
    scorecardDeadlineHours: zod_1.z.number().int('Whole hours only.').min(1).max(168),
    isSelfBookingAllowed: zod_1.z.boolean(),
    panel: zod_1.z.array(exports.panelMemberSchema).max(exports.MAX_PANEL, `At most ${exports.MAX_PANEL} people on a panel.`),
};
function checkPanel(round, ctx) {
    const seen = new Set();
    round.panel.forEach((member, index) => {
        if (seen.has(member.userId)) {
            ctx.addIssue({ code: 'custom', path: ['panel', index, 'userId'], message: 'This person is on the panel twice.' });
        }
        seen.add(member.userId);
    });
    if (round.panel.filter((member) => member.role === 'lead').length > 1) {
        ctx.addIssue({ code: 'custom', path: ['panel'], message: 'A round has one lead at most.' });
    }
    for (const message of panelWeightProblems(round.panel))
        ctx.addIssue({ code: 'custom', path: ['panel'], message });
}
/** A round as step 6 saves it. Its place in the list decides its stage. */
exports.interviewRoundSchema = zod_1.z
    .object({
    /** The row being edited. Kept stable so autosave updates rather than replaces. */
    id: schemas_1.uuidSchema.optional(),
    ...roundShape,
})
    .superRefine(checkPanel);
/** A round as a template stores it — no row of its own. */
exports.templateRoundSchema = zod_1.z.object(roundShape).superRefine(checkPanel);
/** C5: a round's name is its place — "Round 1", "Round 2", "Round 3". Nobody types it. */
function roundNameAt(index) {
    return `Round ${index + 1}`;
}
/** C5-c: every interview is booked for an hour unless that one booking says otherwise. */
exports.DEFAULT_INTERVIEW_MINUTES = 60;
/**
 * A fresh round. C5 keeps only how it's held and the panel on screen; the rest are S12's defaults,
 * still stored (feedback open across rounds, 24h for scorecards). The decision rule, pass mark and
 * blind switch no longer decide anything (§L3 and §L4 as amended by ADR 0077).
 */
function defaultRound(index) {
    return {
        name: roundNameAt(index),
        mode: 'online',
        durationMinutes: exports.DEFAULT_INTERVIEW_MINUTES,
        bufferMinutes: 15,
        minimumNoticeHours: 24,
        rubricId: null,
        interviewKitId: null,
        decisionRule: 'majority',
        isPriorFeedbackVisible: true,
        isBlindScoring: true,
        advanceThreshold: 3,
        scorecardDeadlineHours: 24,
        isSelfBookingAllowed: false,
        panel: [],
    };
}
/**
 * What a round still needs before the job can open (master plan S12: "every round has
 * a panel and a rubric"). `scaleMax` is the chosen rubric's scale, when there is one.
 */
function roundProblems(round, scaleMax) {
    const problems = [...roundPanelProblems(round)];
    const name = round.name.trim() || 'A round';
    if (!round.rubricId) {
        problems.push(`${name}: choose a scorecard rubric`);
    }
    else if (scaleMax !== null && (round.advanceThreshold < exports.RUBRIC_SCALE_MIN || round.advanceThreshold > scaleMax)) {
        problems.push(`${name}: set the pass mark between ${exports.RUBRIC_SCALE_MIN} and ${scaleMax}, the rubric's scale`);
    }
    return problems;
}
/**
 * The panel half of `roundProblems`. In JOB-13 (R3) a round's rubric is made from the scoring
 * matrix, so the matrix section, not the round, reports a missing one.
 */
function roundPanelProblems(round) {
    const problems = [];
    const name = round.name.trim() || 'A round';
    if (!round.panel.some((member) => member.role !== 'observer')) {
        // §L3: observers never count, so a panel of observers cannot decide anything.
        problems.push(`${name}: add at least one panelist or lead`);
    }
    if (round.decisionRule === 'lead_decides' && !round.panel.some((member) => member.role === 'lead')) {
        problems.push(`${name}: "Lead decides" needs a lead on the panel`);
    }
    return problems;
}
/**
 * Step 6. Round n is interview stage n; the list's length switches stages on and off, and zero
 * rounds switches every interview stage off (ADR 0045).
 */
exports.updateJobInterviewPlanSchema = schemas_1.versionedSchema.extend({
    rounds: zod_1.z
        .array(exports.interviewRoundSchema)
        .max(exports.MAX_INTERVIEW_ROUNDS, `At most ${exports.MAX_INTERVIEW_ROUNDS} rounds — one per interview stage.`),
});
// --- Interview plan templates (ADMIN-03) ----------------------------------------------
exports.planTemplateSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(2, 'Name the plan.').max(120),
    description: zod_1.z.string().trim().max(1000).nullable().default(null),
    rounds: zod_1.z
        .array(exports.templateRoundSchema)
        .min(1, 'Add at least one round.')
        .max(exports.MAX_INTERVIEW_ROUNDS, `At most ${exports.MAX_INTERVIEW_ROUNDS} rounds.`),
});
exports.updatePlanTemplateSchema = exports.planTemplateSchema.extend(schemas_1.versionedSchema.shape);
/**
 * "Start from a plan" (JOB-12's round setup): the plan's rounds are copied into the job — how each is held and its panel with
 * the shares. A copy, never a link (#4). Refused once a candidate has reached any round.
 */
exports.applyInterviewPlanSchema = schemas_1.versionedSchema.extend({ planTemplateId: schemas_1.uuidSchema });
/** A round in one line: "Online · 3 people". */
function roundSummary(round) {
    const people = round.panel.length;
    return `${exports.INTERVIEW_MODE_LABELS[round.mode]} · ${people === 0 ? 'no panel yet' : `${people} ${people === 1 ? 'person' : 'people'}`}`;
}
/**
 * ADR 0071 (BH-24): the roles whose holders may be placed on an interview panel. The Approver and Invigilator roles exist for other
 * work, so someone who holds only those is not offered and the server refuses them. HR Lead and Hiring manager stay: the design
 * expects them on panels ("round 3 = HM + HR Lead", E3).
 *
 * ADR 0073 (2026-10-29): Admin (who is also HR) and Leadership (the boss interviews too) may sit on a panel.
 */
exports.PANEL_ROLE_KEYS = ['admin', 'hr_lead', 'leadership', 'recruiter', 'hiring_manager', 'panelist'];
function canSitOnPanel(role) {
    return role !== null && role !== undefined && exports.PANEL_ROLE_KEYS.includes(role);
}
exports.PANEL_NOT_ALLOWED_MESSAGE = "This person doesn't hold a role that interviews here (HR Lead, Recruiter, Hiring manager or Panelist), so they can't sit on a panel.";
/**
 * The marked seats of a plan whose person may not sit on a panel: field paths like `rounds.1.panel.0.userId`, as the server's
 * validation errors use them. `kept[index]` lists who already sits on that round's saved panel: an old panel can still be edited.
 */
function panelProblems(rounds, eligible, kept = []) {
    const fields = {};
    rounds.forEach((round, index) => {
        const keeps = new Set(kept[index] ?? []);
        round.panel.forEach((member, position) => {
            if (!eligible.has(member.userId) && !keeps.has(member.userId))
                fields[`rounds.${index}.panel.${position}.userId`] = exports.PANEL_NOT_ALLOWED_MESSAGE;
        });
    });
    return fields;
}
/**
 * C6-a (DECISIONS-LOG 2026-11-01 Q5): a panelist with no account, added by name and email. An
 * address that already belongs to someone with an account adds that person instead.
 */
exports.addGuestPanelistSchema = zod_1.z.object({
    fullName: zod_1.z.string().trim().min(2, 'Write their name.').max(120),
    email: schemas_1.emailSchema,
});
//# sourceMappingURL=interview-plan.js.map